// Check the kit (T3): line by line, all here, or a line missing, damaged or
// the wrong part. A problem line raises a build issue in Manufacturing.
import { useState } from 'react';

import type { BuildDetailResponse } from '../../api/generated/BuildDetailResponse';
import type { KitLineResponse } from '../../api/generated/KitLineResponse';
import type { KitOutcome } from '../../api/generated/KitOutcome';
import type { useBuildActions } from '../../api/queries';
import { Badge, Button, Dialog, ErrorPanel, TextField } from '../../design/components';
import { formatQuantity } from '../../design/format';
import type { EncodedPhoto } from './photo';
import { encodePhoto } from './photo';

type Problem = Exclude<KitOutcome, 'Unchecked' | 'Present'>;

const PROBLEMS: readonly { value: Problem; label: string }[] = [
    { value: 'Missing', label: 'Missing' },
    { value: 'Damaged', label: 'Damaged' },
    { value: 'WrongPart', label: 'Wrong part' },
];

const OUTCOME_TONE: Record<KitOutcome, 'neutral' | 'success' | 'danger'> = {
    Unchecked: 'neutral',
    Present: 'success',
    Missing: 'danger',
    Damaged: 'danger',
    WrongPart: 'danger',
};

const OUTCOME_LABEL: Record<KitOutcome, string> = {
    Unchecked: 'Not checked',
    Present: 'Here',
    Missing: 'Missing',
    Damaged: 'Damaged',
    WrongPart: 'Wrong part',
};

export function KitCheck({
    build,
    actions,
    canAct,
}: {
    build: BuildDetailResponse;
    actions: ReturnType<typeof useBuildActions>;
    canAct: boolean;
}) {
    const [problem, setProblem] = useState<{ line: KitLineResponse; outcome: Problem } | null>(null);
    const finished = build.kit.finished !== null;
    const unchecked = build.kit.lines.filter((line) => line.outcome === 'Unchecked').length;
    const open = canAct && !finished && build.summary.state !== 'Built';
    return (
        <section className="sf-card sf-stack" aria-labelledby="kit-heading">
            <div className="sf-row" style={{ justifyContent: 'space-between' }}>
                <h2 id="kit-heading">Check the kit</h2>
                {finished ? (
                    <Badge tone="success">Checked by {build.kit.finished?.displayName}</Badge>
                ) : (
                    <Badge>{unchecked} to check</Badge>
                )}
            </div>
            {build.kit.lines.length === 0 ? <p className="sf-muted">This build’s kit has no lines.</p> : null}
            <ol className="sf-stack" style={{ paddingLeft: 0, listStyle: 'none', margin: 0 }}>
                {build.kit.lines.map((line) => (
                    <li key={line.number} className="sf-kit-line">
                        <div>
                            <strong className="sf-mono">{line.partNumber}</strong> {line.name ?? ''}{' '}
                            <span className="sf-muted">· {formatQuantity(line.quantity, line.unitOfMeasure)}</span>
                            {line.note ? <div className="sf-muted">“{line.note}”</div> : null}
                        </div>
                        <div className="sf-outcomes">
                            <Badge tone={OUTCOME_TONE[line.outcome]}>{OUTCOME_LABEL[line.outcome]}</Badge>
                            {open ? (
                                <>
                                    <Button
                                        aria-label={`${line.partNumber} is here`}
                                        disabled={actions.recordKitLine.isPending}
                                        onClick={() => {
                                            actions.recordKitLine.mutate({
                                                line: line.number,
                                                body: {
                                                    outcome: 'Present',
                                                    note: null,
                                                    photoBase64: null,
                                                    photoContentType: null,
                                                },
                                            });
                                        }}
                                    >
                                        Here
                                    </Button>
                                    {PROBLEMS.map((option) => (
                                        <Button
                                            key={option.value}
                                            aria-label={`${line.partNumber} is ${option.label.toLowerCase()}`}
                                            onClick={() => {
                                                setProblem({ line, outcome: option.value });
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </>
                            ) : null}
                        </div>
                    </li>
                ))}
            </ol>
            {actions.recordKitLine.isError ? <ErrorPanel error={actions.recordKitLine.error} /> : null}
            {open ? (
                <div className="sf-row">
                    <Button
                        large
                        disabled={unchecked === 0 || actions.allHere.isPending}
                        onClick={() => {
                            actions.allHere.mutate();
                        }}
                    >
                        All here
                    </Button>
                    <Button
                        large
                        variant="primary"
                        disabled={unchecked > 0 || actions.finishKitCheck.isPending}
                        onClick={() => {
                            actions.finishKitCheck.mutate();
                        }}
                    >
                        Finish the check
                    </Button>
                </div>
            ) : null}
            {actions.finishKitCheck.isError ? <ErrorPanel error={actions.finishKitCheck.error} /> : null}
            {problem ? (
                <KitProblemDialog
                    line={problem.line}
                    outcome={problem.outcome}
                    busy={actions.recordKitLine.isPending}
                    error={actions.recordKitLine.error}
                    onClose={() => {
                        setProblem(null);
                    }}
                    onSubmit={(note, photo) => {
                        actions.recordKitLine.mutate(
                            {
                                line: problem.line.number,
                                body: {
                                    outcome: problem.outcome,
                                    note,
                                    photoBase64: photo?.base64 ?? null,
                                    photoContentType: photo?.contentType ?? null,
                                },
                            },
                            {
                                onSuccess: () => {
                                    setProblem(null);
                                },
                            },
                        );
                    }}
                />
            ) : null}
        </section>
    );
}

function KitProblemDialog({
    line,
    outcome,
    busy,
    error,
    onClose,
    onSubmit,
}: {
    line: KitLineResponse;
    outcome: Problem;
    busy: boolean;
    error: unknown;
    onClose: () => void;
    onSubmit: (note: string, photo: EncodedPhoto | null) => void;
}) {
    const [note, setNote] = useState('');
    const [photo, setPhoto] = useState<EncodedPhoto | null>(null);
    return (
        <Dialog open title={`${line.partNumber}: ${OUTCOME_LABEL[outcome]}`} onClose={onClose}>
            <p className="sf-muted">
                This raises a build issue in Manufacturing, where the planner and the engineer will see it.
            </p>
            <TextField label="Note" value={note} onChange={setNote} multiline />
            <div className="sf-field">
                <label htmlFor="kit-photo">Photo (optional)</label>
                <input
                    id="kit-photo"
                    className="sf-input"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                            encodePhoto(file).then(setPhoto, () => {
                                setPhoto(null);
                            });
                        }
                    }}
                />
            </div>
            {error ? <ErrorPanel error={error} title="The line wasn’t recorded." /> : null}
            <div className="sf-dialog-actions">
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() => {
                        onSubmit(note, photo);
                    }}
                >
                    {busy ? 'Recording…' : `Record ${OUTCOME_LABEL[outcome].toLowerCase()}`}
                </Button>
            </div>
        </Dialog>
    );
}
